// Identifiers
export type BrandId = string & { __brand: "BrandId" };
export type ProductId = string & { __brand: "ProductId" };
export type BrokerId = string & { __brand: "BrokerId" };
export type CustomerId = string & { __brand: "CustomerId" };
export type OrderId = string & { __brand: "OrderId" };
export type LeadId = string & { __brand: "LeadId" };
export type TicketId = string & { __brand: "TicketId" };

// Enums
export type MaaiStage = 1 | 2 | 3 | 4;
export type OrderStatus = "pending" | "paid" | "delivered" | "rejected" | "cancelled";
export type PaymentMethod = "COD" | "QR" | "CARD" | "TELESALES";
export type LeadStatus = "pending" | "calling" | "closed" | "rejected";
export type TicketStatus = "open" | "in_progress" | "closed";
export type BrandStatus = "active" | "pending" | "suspended";
export type PortalRole =
  | "co-founder"
  | "marketing"
  | "support"
  | "supplier"
  | "broker"
  | "telesales"
  | "customer";

// Core entities mirror the prototype store shape, cleaned for production swap.
export type Brand = {
  id: BrandId;
  name: string;
  gp: number;
  status: BrandStatus;
  category: string;
  bankAcc: string;
  regDoc: string;
  joined: number;
  isHouse: boolean;
};

export type Product = {
  id: ProductId;
  brandId: BrandId;
  name: string;
  price: number;
  stock: number;
  sold: number;
  telesalesDiscount: number;
  description?: string;
};

export type Broker = {
  id: BrokerId;
  name: string;
  level: number;
  exp: number;
  totalEarn: number;
  withdrawn: number;
  avatar: string;
  joined: number;
};

export type Customer = {
  id: CustomerId;
  name: string;
  phone: string;
  addr: string;
};

export type Order = {
  id: OrderId;
  customerId: CustomerId;
  productId: ProductId;
  productName: string;
  brokerId: BrokerId | null;
  brandId: BrandId;
  maai: MaaiStage;
  amount: number;
  status: OrderStatus;
  paymentMethod: PaymentMethod;
  isHouseBrand?: boolean;
  createdAt: number;
};

export type Lead = {
  id: LeadId;
  orderId: OrderId;
  customerId: CustomerId;
  productId: ProductId;
  brokerId: BrokerId | null;
  status: LeadStatus;
  assignedTo: string | null;
  createdAt: number;
  attempts: number;
  closedAt?: number;
  reason?: string;
};

export type Ticket = {
  id: TicketId;
  subject: string;
  category: string;
  priority: "low" | "medium" | "high";
  status: TicketStatus;
  openedBy: string;
  createdAt: number;
  messages: Array<{
    from: "customer" | "broker" | "brand" | "support";
    text: string;
    at: number;
  }>;
};

export type Coupon = {
  id: string;
  code: string;
  desc: string;
  value: number;
  scope: "telesales" | "customer";
  used: number;
  limit: number;
  status: "active" | "expired";
  createdAt: number;
};

export type Campaign = {
  id: string;
  name: string;
  budget: number;
  spent: number;
  gmv: number;
  status: "active" | "paused";
  channel: string;
  startsAt: number;
};

export type Settlement = {
  id: string;
  periodStart: number;
  periodEnd: number;
  gmv: number;
  fixedCost: number;
  gatewayFee: number;
  marketingSpent: number;
  netProfit: number;
  paidOut: boolean;
  paidAt?: number;
};

export type SupplierPayout = {
  brandId: BrandId;
  gross: number;
  supplierShare: number;
  withdrawn: number;
  history?: Array<{ d: number; a: number; ref: string; status: "success" | "pending" }>;
};

export type AffiliateTemplate = {
  id: string;
  name: string;
  category: string;
  uses: number;
};

export type FlaggedPost = {
  id: string;
  brokerId: BrokerId;
  excerpt: string;
  reason: string;
  reportedAt: number;
  status: "pending" | "removed" | "ignored";
};

export type PushLog = {
  id: string;
  title: string;
  body: string;
  sentAt: number;
  reach: number;
};

export type ScenarioId =
  | "normal"
  | "high-gmv"
  | "lead-backlog"
  | "low-stock"
  | "supplier-dispute"
  | "payout-pending"
  | "campaign-spike"
  | "payment-fail"
  | "pdpa-decline";

export type YJSettings = {
  gpDefault: number;
  primaryColor: string;
  theme: "light" | "dark";
  coFounderSplit: { yiw: number; dev: number };
};

export type EntityCounters = {
  brand: number;
  product: number;
  broker: number;
  customer: number;
  order: number;
  lead: number;
  ticket: number;
  coupon: number;
  campaign: number;
  push: number;
  event: number;
};

export type MockClock = {
  seed: string;
  now: number;
  stepMs: number;
};

export type ScenarioConfig = {
  paymentFailEvery?: number;
  pdpaDeclined?: boolean;
  agents?: string[];
  notes?: string[];
};

export const brandId = (value: string) => value as BrandId;
export const productId = (value: string) => value as ProductId;
export const brokerId = (value: string) => value as BrokerId;
export const customerId = (value: string) => value as CustomerId;
export const orderId = (value: string) => value as OrderId;
export const leadId = (value: string) => value as LeadId;
export const ticketId = (value: string) => value as TicketId;
